import type { Mimir, Collection } from "bildebanken-model";
import { getApiUrl } from "config";
import { apiFetch } from "services/fetch-wrapper";
import type { DraggableImage } from "services/folders/types";

export class ConflictError extends Error {}

export async function addUserToCollection(collectionId: string): Promise<Response["status"]> {
  const response = await apiFetch(`${getApiUrl()}/collections/${collectionId}/adduser`, {
    method: "POST",
  });
  if (!response.ok) {
    throw Error(`Could not add user to collection '${collectionId}'. ${response.body}`);
  }
  return response.status;
}

export async function removeUserFromCollection(
  collectionId: string,
  userId: string,
): Promise<Mimir.AssetVisibility> {
  const response = await apiFetch(`${getApiUrl()}/collections/${collectionId}/removeuser`, {
    method: "POST",
    body: JSON.stringify({ userId }),
    headers: {
      "content-type": "application/json",
    },
  });
  if (!response.ok) {
    throw Error(`Could not remove user from collection '${collectionId}'. ${response.body}`);
  }
  const responseBody = await response.json();
  return responseBody.updatedVisibleTo;
}

export async function getCollections(
  sorting?: Mimir.FolderSort,
  signal?: AbortSignal,
): Promise<Collection[]> {
  const url = new URL(`${getApiUrl()}/collections`);
  if (sorting?.sortOrder) url.searchParams.set("sortOrder", sorting.sortOrder);
  if (sorting?.sortProperty) url.searchParams.set("sortProperty", sorting.sortProperty);
  const response = await apiFetch(url.toString(), { method: "GET", signal });

  if (!response.ok) {
    if (response.status === 403) throw Error("No access to this page");
    throw Error("Could not get collections", { cause: response.body });
  }
  return await response.json();
}

export async function createCollection(
  name: string,
  visibleTo: Mimir.AssetVisibility,
): Promise<Collection> {
  const response = await apiFetch(`${getApiUrl()}/collections`, {
    method: "POST",
    body: JSON.stringify({ name, visibleTo }),
    headers: {
      "content-type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 409) {
      throw new ConflictError(`Collection '${name}' already exists`);
    }
    throw Error(`Could not create collection '${name}'. ${response.body}`);
  }
  const folderData = await response.json();

  const updatedFolder = await apiFetch(`${getApiUrl()}/folders/${folderData.id}/metadata`, {
    method: "PUT",
    body: JSON.stringify({
      metadataDelta: { formId: "default_folder", formData: { displayName: name } },
    }),
    headers: {
      "content-type": "application/json",
    },
  });
  const updatedCollection: Collection = await updatedFolder.json();
  return { ...folderData, metadata: updatedCollection.metadata } as Collection;
}

export async function addItemToCollection(collectionId: string, itemId: string) {
  const response = await apiFetch(`${getApiUrl()}/collections/${collectionId}/content`, {
    method: "PUT",
    body: JSON.stringify({ id: itemId }),
    headers: {
      "content-type": "application/json",
    },
  });

  if (!response.ok) {
    throw Error(`Could not add item to collection '${collectionId}'. ${response.body}`);
  }
}

export async function getCollection(collectionId: string): Promise<Collection> {
  const response = await apiFetch(`${getApiUrl()}/collections/${collectionId}`, { method: "GET" });

  if (!response.ok) {
    throw Error(`Could not fetch collection '${collectionId}'. ${response.body}`);
  }

  return response.json();
}

export async function updateCollection(
  collectionId: string,
  metadata: { name: string },
): Promise<Collection> {
  const response = await apiFetch(`${getApiUrl()}/collections/${collectionId}`, {
    method: "PATCH",
    body: JSON.stringify(metadata),
    headers: {
      "content-type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 409) {
      throw new ConflictError(`Collection '${metadata.name ?? collectionId}' already exists`);
    }
    throw Error(`Could not update collection '${collectionId}'. ${response.body}`);
  }
  return response.json();
}

export async function deleteCollection(collectionId: string) {
  const response = await apiFetch(`${getApiUrl()}/collections/${collectionId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw Error(`Could not delete collection '${collectionId}'. ${response.body}`);
  }
}

export async function removeItemFromCollection(collectionId: string, itemId: string) {
  const url = new URL(`${getApiUrl()}/collections/${collectionId}/content`);
  url.searchParams.append("id", itemId);

  const response = await apiFetch(url.toString(), { method: "DELETE" });

  if (!response.ok) {
    throw Error(
      `Could not delete item '${itemId}' from collection '${collectionId}'. ${response.body}`,
    );
  }
}

export async function getCollectionContent(collectionId: string): Promise<DraggableImage[]> {
  const response = await apiFetch(`${getApiUrl()}/collections/${collectionId}/content`, {
    method: "GET",
  });

  if (!response.ok) {
    throw Error(`Could not fetch content for collection '${collectionId}'. ${response.body}`);
  }

  return response.json();
}
